import { useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { fetchPCConsoleCompareTrendsResultsPlus } from '../services';
import { analyseGamesMetricsTrendsData } from '../utils';
import { ComposedDateMode } from '@/pages/intelligence/components/ComposedDatePicker/enums';

import type { IDateValue } from '@/pages/intelligence/types/common';
import type {
  IFilter,
  IPCConsoleGame,
  IPCConsoleGamesCumulativeMetric,
  IRegionValueDataOfRegionSelector,
  IPCConsoleGamesCompareTrendsResult,
} from '@/pages/intelligence/types/pcConsoleCompare';
import type { IComposedDateData } from '@/pages/intelligence/components/ComposedDatePicker/types';

export default function useCompareTrends(option: {
  entityTypes?: string[];
  // 所有的devices
  devices: string[];
  // 对比的游戏数据
  games: IPCConsoleGame[];
  // 筛选器数据
  filter: IFilter;
  // 组合时间筛选数据，目前分相对时间、绝对时间两种
  composedDateData: IComposedDateData;
  setComposedDateData: React.Dispatch<React.SetStateAction<IComposedDateData>>;
  setDownloadDateValue: React.Dispatch<React.SetStateAction<IDateValue>>;
  fetchCompareTrendsResults?: (params: {
    games: IPCConsoleGame[];
    metrics: string[];
    devices?: string[];
    segments?: string[];
    regionData?: IRegionValueDataOfRegionSelector;
    composedDateData: IComposedDateData;
  }) => Promise<{ trends: IPCConsoleGamesCompareTrendsResult[]; period: string; startDate: string; endDate: string }>;
}) {
  const {
    entityTypes,
    devices,
    games,
    filter,
    composedDateData,
    setComposedDateData,
    setDownloadDateValue,
    fetchCompareTrendsResults,
  } = option || {};

  const [isLoading, setIsLoading] = useState(true);
  const [compareResult, setCompareResult] = useState<IPCConsoleGamesCompareTrendsResult[]>([]);
  const compareTrendsData = useMemo<{
    trendsDimensions: string[];
    trendsXAxisData: string[];
    trendsCumulativeMetricsDataMap: {
      [key: string]: IPCConsoleGamesCumulativeMetric[];
    };
    trendsData: any[];
  }>(() => {
    const gamesMetricsCompareTrendsData = analyseGamesMetricsTrendsData(compareResult, {
      mode: composedDateData?.mode,
      metrics: filter?.metrics,
    });

    return {
      trendsDimensions: gamesMetricsCompareTrendsData?.dimensions,
      trendsXAxisData: gamesMetricsCompareTrendsData?.xAxisData,
      trendsCumulativeMetricsDataMap: gamesMetricsCompareTrendsData?.cumulativeMetricsDataMap,
      trendsData: gamesMetricsCompareTrendsData?.sources,
    };
  }, [compareResult, composedDateData?.mode, filter?.metrics]);

  // 根据具体每个游戏的、每个指标 是否是有效数值 来判断 可视化 是否有数据
  // const isNoData = useMemo<boolean>(() => {
  //   const metrics = filter?.metrics;
  //   if (!trendsData?.length || !metrics?.length) return true;
  //   const flag = true;
  //   for (const trendData of trendsData) {
  //     for (const metric of metrics) {
  //       const metricValue = Number.parseFloat(trendData?.[metric]);
  //       if (!Number.isNaN(metricValue)) {
  //         return false;
  //       }
  //     }
  //   }
  //   return flag;
  // }, [trendsData, filter?.metrics]);

  // 根据具体每个游戏的数量来判断是否有数据，有勾选游戏  就有可视化
  const isNoData = !compareTrendsData?.trendsData?.length;

  const fetcher =
    typeof fetchCompareTrendsResults === 'function'
      ? fetchCompareTrendsResults
      : fetchPCConsoleCompareTrendsResultsPlus;

  const {
    // run: fetchPCUCompareTrendsResultsPlusRun,
    runAsync: fetchPCUCompareTrendsResultsPlusRunAsync,
    cancel: fetchPCUCompareTrendsResultsPlusCancel,
  } = useRequest(fetcher, { manual: true });

  const updateCompareTrendsData = async (params?: {
    games?: IPCConsoleGame[];
    filter?: IFilter;
    composedDateData?: IComposedDateData;
    callback?: (res: any) => void;
  }): Promise<void> => {
    if (isLoading) fetchPCUCompareTrendsResultsPlusCancel();
    setIsLoading(true);
    const gamesTemp = params?.games || games;
    const filterTemp = params?.filter || filter;
    const composedDateDataTemp = params?.composedDateData || composedDateData;
    const res = await fetchPCUCompareTrendsResultsPlusRunAsync(
      {
        entityTypes,
        games: gamesTemp,
        metrics: filterTemp?.metrics,
        segments: filterTemp?.segments,
        devices: filterTemp?.devices,
        regionData: filterTemp?.regionData,
        composedDateData: composedDateDataTemp,
      },
      { devices },
    );

    // 绝对时间时，需要回填接口返回的时间
    if (composedDateDataTemp?.mode === ComposedDateMode.Absolute) {
      const periodTemp = res?.period || 'day';
      const dateTypeTemp = periodTemp === 'day' ? 'date' : periodTemp;
      const startDateTemp = res?.startDate || '';
      const endDateTemp = res?.endDate || '';
      setDownloadDateValue([startDateTemp, endDateTemp]);
      setComposedDateData({
        ...composedDateDataTemp,
        dateType: dateTypeTemp,
        dateValue: [startDateTemp, endDateTemp],
      });
    }

    setCompareResult(res?.trends || []);
    setIsLoading(false);
    if (typeof params?.callback === 'function') {
      params?.callback?.(res);
    }
  };

  const handleDeleteGames = (ids: string[]) => {
    const compareResultTemp = compareResult?.filter((item) => !ids?.includes(item?.game_id)) || [];
    setCompareResult(compareResultTemp);
  };

  return {
    isLoading,
    isNoData,
    compareTrendsData,
    updateCompareTrendsData,
    onDeleteGames: handleDeleteGames,
  };
}
